/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { useCallback, useEffect, useRef } from 'react';
import reactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import OutsideAlerter from '../OutsideAlerter/OutsideAlerter';
import './Modal.css';

interface Props {
  children: JSX.Element;
  isOpen: boolean;
  close: () => void;
  side?: string;
  openBtn: HTMLButtonElement | null;
  closeCallback: React.RefObject<{ f: () => void }>;
}

function Modal({ children, isOpen, close, side = 'center', openBtn, closeCallback }: Props) {
  const navigate = useNavigate();
  const nodeRef = useRef<HTMLDivElement>(null);
  const modalContainer: HTMLElement | null = document.getElementById('modal');
  const focusableElementsRef = useRef<{ elements: Element[] }>({
    elements: [],
  });
  const fromPop = useRef<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;
    fromPop.current = false;
    window.history.pushState({}, '');
    window.addEventListener('popstate', () => {
      fromPop.current = true;
      close();
    });

    return () => {
      window.removeEventListener('popstate', () => {
        fromPop.current = false;
        close();
      });
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !nodeRef.current) return;
    focusableElementsRef.current.elements = [
      ...nodeRef.current.querySelectorAll(
        'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
  }, [isOpen]);

  const handleModalEnter = useCallback(() => {
    const header = document.querySelector('.header') as HTMLDivElement;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'clip';
    document.body.style.inset = `${document.body.getBoundingClientRect().top}px 0 0 0`;
    document.body.style.insetInlineEnd = `${scrollbarWidth}px`;
    document.body.style.position = 'fixed';
    if (header) {
      header.style.width = `calc(100% - ${scrollbarWidth}px)`;
    }
  }, []);

  const handleModalClosed = useCallback(() => {
    const header = document.querySelector('.header') as HTMLDivElement;

    const y = document.body.getBoundingClientRect().top;
    document.body.style.inset = '';
    document.body.style.overflow = '';
    document.body.style.insetInlineEnd = '';
    document.body.style.position = '';
    window.scrollTo(0, -y);
    if (header) {
      header.style.width = '';
    }
    if (openBtn) {
      setTimeout(() => {
        openBtn.focus({
          preventScroll: true,
        });
      }, 0);
    }
    if (closeCallback.current) closeCallback.current.f();
    if (!fromPop.current) navigate(-1);
  }, [openBtn, fromPop.current]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleKeyDown = useCallback((e: any) => {
    if (!nodeRef.current) return;
    const { elements } = focusableElementsRef.current;
    const { length } = elements;

    if (length === 2) e.preventDefault();

    if (!e.shiftKey && e.keyCode === 9) {
      if (
        e.target === elements[length - 2] ||
        e.target === elements[length - 1] ||
        // Check if it is a dropdown item
        e.target.parentNode.previousSibling === elements[length - 2]
      ) {
        e.preventDefault();
        setTimeout(() => {
          (elements[1] as HTMLElement).focus();
        }, 0);
      }
    }

    if (e.shiftKey && e.keyCode === 9) {
      if (e.target === elements[1] || e.target === elements[0]) {
        (elements[length - 1] as HTMLElement).focus();
      }
    }

    if (e.keyCode === 27) close();
  }, []);

  useEffect(() => {
    if (!nodeRef.current) return;

    if (focusableElementsRef.current.elements?.length) {
      (focusableElementsRef.current.elements[1] as HTMLElement).focus();
    }

    if (isOpen) {
      nodeRef.current?.addEventListener('keydown', handleKeyDown);
    } else {
      nodeRef.current?.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      nodeRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown, nodeRef]);

  if (!modalContainer) return null;

  return reactDOM.createPortal(
    <CSSTransition
      nodeRef={nodeRef}
      in={isOpen}
      timeout={300}
      classNames="modal"
      onExited={handleModalClosed}
      onEnter={handleModalEnter}
      unmountOnExit
    >
      <div
        className={`modal${side === 'right' ? ' modal--right' : ''}`}
        ref={nodeRef}
        onClick={close}
        role="presentation"
      >
        <div className="modal__overflow" onClick={(e) => e.stopPropagation()} role="presentation">
          <OutsideAlerter onClick={close}>
            <span tabIndex={0} />
            <div className="modal__container" role="dialog">
              {children}
            </div>
            <span tabIndex={0} />
          </OutsideAlerter>
        </div>
      </div>
    </CSSTransition>,
    modalContainer,
  );
}

export default Modal;
